import { useLocalStorage } from "../../../hooks";
import { RememberedContactDetails } from "../../../types";

const LOCAL_STORAGE_KEY = "provetContactDetails";

/**
 * Hook for managing contact details persistence in localStorage.
 *
 * This hook is a specialized wrapper around the generic `useLocalStorage` hook,
 * configured specifically for `RememberedContactDetails` type.
 *
 * **Features:**
 * - Type-safe operations for RememberedContactDetails
 * - Automatic JSON serialization/deserialization
 * - Silent error handling for storage failures
 * - SSR-safe (won't crash on server)
 *
 * @returns Object with save, load, remove functions and the storage key
 *
 * @example
 * ```tsx
 * const { saveContactDetails, loadContactDetails, removeContactDetails } =
 *   useContactDetailsLocalStorage();
 *
 * // Save data
 * saveContactDetails({
 *   rememberedDetailsChecked: true,
 *   client: { ... },
 *   patient: { ... }
 * });
 *
 * // Load data
 * const saved = loadContactDetails();
 * if (saved) {
 *   console.log(saved.client.firstName);
 * }
 *
 * // Remove data
 * removeContactDetails();
 * ```
 */
export function useContactDetailsLocalStorage() {
  const { save, load, remove, key } =
    useLocalStorage<RememberedContactDetails>(LOCAL_STORAGE_KEY);

  return {
    saveContactDetails: save,
    loadContactDetails: load,
    removeContactDetails: remove,
    LOCAL_STORAGE_KEY: key
  };
}
